import { User, UserRole } from '../types';
import { SCHOOL_CODE_METADATA_KEY } from '../lib/pendingSchoolCode';
import { supabase } from '../lib/supabase';
import { reportError } from '../lib/errorHandling';

export type RegisterResult =
  | { user: User; needsEmailConfirmation: false }
  | { user: null; needsEmailConfirmation: true };

/** Verdict from the auth_email_status RPC — what an email is, from the asking tenant's point of view. */
export type EmailStatus = 'none' | 'this_tenant' | 'other_tenant';

/**
 * Thrown by login() when the password was CORRECT but the account belongs to a
 * different tenant than the site it was entered on. Carrying the home tenant's
 * slug/name is deliberate: the caller proved they own the account, so naming
 * the site it lives on is help, not a leak — the old behaviour ("Invalid email
 * or password") told someone with the right password that their password was
 * wrong.
 */
export class TenantMismatchError extends Error {
  code = 'tenant_mismatch' as const;
  tenantSlug: string | null;
  tenantName: string | null;
  constructor(tenantSlug: string | null, tenantName: string | null) {
    super('This account belongs to a different site');
    this.name = 'TenantMismatchError';
    this.tenantSlug = tenantSlug;
    this.tenantName = tenantName;
  }
}

/**
 * Where an emailed auth link should come back to. Supabase falls back to the
 * project Site URL — the apex — whenever a link is sent without one, which
 * strands someone who signed up (or asked for a magic link) on a tenant
 * subdomain: they land on a host that knows nothing about their tenant.
 *
 * `/login` is the right target for both: the session is established from the
 * URL hash, and Login's isAuthenticated guard then routes by role.
 *
 * ⚠️ The origin must be covered by the Supabase auth Redirect URL allow-list
 * (`https://*.kulalearn.com/**`). An origin that isn't on it is silently
 * ignored and the link falls back to the Site URL — i.e. the exact bug this
 * exists to fix, with nothing surfaced anywhere. Custom tenant domains need
 * their own allow-list entry.
 *
 * Returns undefined outside the browser (tests, SSR) so callers can spread it.
 */
function emailLinkRedirect(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return `${window.location.origin}/login`;
}

// Helper function to fetch profile with retry
async function fetchProfileWithRetry(userId: string, maxRetries = 3, delayMs = 500): Promise<any> {
  for (let i = 0; i < maxRetries; i++) {
    const { data: profile, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    if (!error) {
      return profile;
    }

    // If last attempt, throw error
    if (i === maxRetries - 1) {
      throw new Error(`Profile fetch failed after ${maxRetries} attempts: ${error.message}`);
    }

    // Wait before retrying
    await new Promise(resolve => setTimeout(resolve, delayMs));
  }
}

export const authService = {
  async login(email: string, password: string, expectedTenantId?: string): Promise<User> {
    let { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    // A password copied out of an email (the welcome email's credentials
    // table, above all) very often brings a trailing space, tab or line break
    // with it, and GoTrue treats that as part of the password, so a correct
    // temporary password reads as "Invalid email or password". Retry once
    // without the surrounding whitespace. The password exactly as typed is
    // always tried FIRST, so a real password that starts or ends with a space
    // still works; the retry only ever runs after a refusal.
    const trimmed = password.trim();
    if (error?.code === 'invalid_credentials' && trimmed && trimmed !== password) {
      ({ data, error } = await supabase.auth.signInWithPassword({ email, password: trimmed }));
    }

    if (error) {
      // Keep GoTrue's stable `code` (e.g. email_not_confirmed) on the thrown
      // error: Login branches on it to offer a resend, and the prose message
      // is reworded between releases.
      const err = new Error(error.message) as Error & { code?: string };
      err.code = error.code;
      throw err;
    }
    if (!data.user) throw new Error('Login failed');

    const profile = await fetchProfileWithRetry(data.user.id);

    // Enforce tenant isolation: reject login if user doesn't belong to this
    // tenant — but SAY so. Resolve the account's home tenant BEFORE signing
    // out (the read runs as authenticated; best-effort, the error degrades to
    // slug/name = null and the UI shows a generic cross-tenant notice).
    if (expectedTenantId && !profile.is_super_admin && profile.tenant_id !== expectedTenantId) {
      let homeSlug: string | null = null;
      let homeName: string | null = null;
      try {
        // supabase-js RETURNS query errors rather than throwing, so the error
        // has to be re-thrown for the catch below to see it — otherwise this
        // degrades to a nameless banner with nothing reported anywhere.
        const { data: home, error: homeError } = await supabase
          .from('tenants')
          .select('slug, name')
          .eq('id', profile.tenant_id)
          .maybeSingle();
        if (homeError) throw new Error(homeError.message);
        homeSlug = home?.slug ?? null;
        homeName = home?.name ?? null;
      } catch (err) {
        reportError('authService.login.resolveHomeTenant', err);
      }
      await supabase.auth.signOut();
      throw new TenantMismatchError(homeSlug, homeName);
    }

    return {
      id: profile.id,
      name: profile.name,
      email: profile.email,
      role: profile.role as UserRole,
      avatarUrl: profile.avatar_url || undefined,
      tenantId: profile.tenant_id,
      isSuperAdmin: profile.is_super_admin || false,
    };
  },

  async register(
    name: string,
    email: string,
    _role: UserRole,
    password: string,
    tenantSlug?: string,
    schoolCode?: string | null,
  ): Promise<RegisterResult> {
    // SECURITY: role is always STUDENT at signup. Role escalation requires admin action.
    // tenant_slug (not UUID) is passed so the DB trigger validates it against the tenants table.
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          ...(tenantSlug ? { tenant_slug: tenantSlug } : {}),
          // A school code the learner arrived with. Kept on the account so it
          // survives the activation link opening in another browser; it only
          // prefills the redeem form (lib/pendingSchoolCode.ts).
          ...(schoolCode ? { [SCHOOL_CODE_METADATA_KEY]: schoolCode } : {}),
        },
        // Keeps the confirmation link on THIS tenant's subdomain — see emailLinkRedirect.
        emailRedirectTo: emailLinkRedirect(),
      }
    });

    if (error) throw new Error(error.message);
    if (!data.user) throw new Error('Registration failed');

    // GoTrue anti-enumeration: signing up with an email that ALREADY has an
    // account returns a fake success — a user object with an empty identities
    // array and no session — and sends no email. Passing that through as
    // needsEmailConfirmation shows "check your inbox" for an email that will
    // never come. Name it instead; Login's copy map turns the code into
    // "an account with this email already exists".
    if (!data.session && (data.user.identities?.length ?? 0) === 0) {
      const err = new Error('An account with this email already exists') as Error & { code?: string };
      err.code = 'user_already_exists';
      throw err;
    }

    // Email confirmation is enabled when signUp returns no session.
    // The DB trigger handle_new_user has already created the public.users row,
    // but without a session we can't read it back (RLS requires auth.uid()).
    // Surface this to the UI so it can show a "check your inbox" state instead
    // of trying to log the user in.
    if (!data.session) {
      return { user: null, needsEmailConfirmation: true };
    }

    const userId = data.user.id;

    // Resolve tenant_id from slug for the fallback insert
    let tenantId: string | undefined;
    if (tenantSlug) {
      const { data: tenantData } = await supabase
        .from('tenants')
        .select('id')
        .eq('slug', tenantSlug)
        .single();
      tenantId = tenantData?.id;
    }

    // Manually create user profile in case trigger didn't fire
    try {
      // SECURITY: Fallback insert always uses STUDENT role.
      const { error: insertError } = await supabase
        .from('users')
        .insert({
          id: userId,
          email,
          name,
          role: 'STUDENT' as UserRole,
          avatar_url: `https://api.dicebear.com/7.x/thumbs/svg?seed=${email}`,
          ...(tenantId ? { tenant_id: tenantId } : {}),
        });

      if (insertError) {
        console.warn('Insert error (might be duplicate):', insertError);
        // If it's a duplicate key error, that's ok - the trigger already created it
        // Otherwise throw
        if (!insertError.message.includes('duplicate')) {
          throw insertError;
        }
      }
    } catch (err) {
      // Best-effort: the DB trigger may have already created the profile.
      reportError('authService.signup.createProfile', err, { userId });
    }

    // Fetch the created profile
    const profile = await fetchProfileWithRetry(userId, 3, 200);

    return {
      user: {
        id: profile.id,
        name: profile.name,
        email: profile.email,
        role: profile.role as UserRole,
        avatarUrl: profile.avatar_url || undefined,
        tenantId: profile.tenant_id,
        isSuperAdmin: profile.is_super_admin || false,
      },
      needsEmailConfirmation: false,
    };
  },

  /**
   * Magic link is a SIGN-IN control, and until Aug 2026 it quietly doubled as a
   * signup one. `signInWithOtp` defaults to `shouldCreateUser: true`, so anyone
   * who typed an address here got a fresh auth user carrying EMPTY metadata —
   * no name, no `tenant_slug`. `handle_new_user` had nothing to resolve a
   * tenant from and fell back to a hard-coded id, so the account was created
   * against the WRONG tenant. The emailed link then authenticated perfectly and
   * AuthContext's tenant guard signed them straight back out — a login page
   * that can never let them in, with no error surfaced anywhere. It cost one
   * real visitor 21 minutes and two accounts before she gave up.
   *
   * Both halves are load-bearing:
   *   - `shouldCreateUser: false` — a sign-in control must never mint accounts.
   *     Someone with no account now gets a real error instead of a broken one.
   *   - `tenant_slug` — so the trigger can still attribute the row on any path
   *     where a user IS created. The flag is Supabase's to change; the
   *     attribution is ours to get right.
   */
  async sendMagicLink(email: string, tenantSlug?: string): Promise<void> {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
        ...(tenantSlug ? { data: { tenant_slug: tenantSlug } } : {}),
        // Keeps the sign-in link on THIS tenant's subdomain — see emailLinkRedirect.
        emailRedirectTo: emailLinkRedirect(),
      },
    });
    if (error) {
      // Keep GoTrue's stable error `code` (e.g. otp_disabled) on the thrown
      // error — Login's copy map matches on it, and the prose message is not a
      // stable API.
      const err = new Error(error.message) as Error & { code?: string };
      err.code = error.code;
      throw err;
    }
  },

  /**
   * Re-send the account activation email to an address that signed up but has
   * not confirmed yet. Sign-in refuses an unconfirmed account (GoTrue
   * `email_not_confirmed`), and until this existed the only way to get a second
   * email was to try signing up again — which GoTrue answers with a silent fake
   * success for an existing address, so nothing arrived. Rate-limited by
   * GoTrue; the error surfaces through Login's copy map like every other one.
   */
  async resendConfirmation(email: string): Promise<void> {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      // Keeps the activation link on THIS tenant's subdomain — see emailLinkRedirect.
      options: { emailRedirectTo: emailLinkRedirect() },
    });
    if (error) {
      const err = new Error(error.message) as Error & { code?: string };
      err.code = error.code;
      throw err;
    }
  },

  /**
   * Pre-flight for the login page: what is this email, from THIS tenant's
   * point of view? Backed by the anon-callable auth_email_status RPC
   * (20260818000002) because anon has no SELECT on users.
   *
   * Best-effort by design: returns null (unknown) on any failure so callers
   * fall through to GoTrue's own behaviour — a broken helper must never be
   * able to block sign-in or signup.
   */
  async emailStatus(email: string, tenantSlug: string): Promise<EmailStatus | null> {
    try {
      const { data, error } = await supabase.rpc('auth_email_status', {
        p_email: email,
        p_tenant_slug: tenantSlug,
      });
      if (error) throw new Error(error.message);
      if (data === 'none' || data === 'this_tenant' || data === 'other_tenant') return data;
      // Still returns null (the caller must not be blocked), but a verdict the
      // RPC should never produce is degradation, not a normal outcome — the
      // house rule is that best-effort paths report rather than go quiet.
      throw new Error(`auth_email_status returned an unexpected verdict: ${JSON.stringify(data)}`);
    } catch (err) {
      reportError('authService.emailStatus', err, { tenantSlug });
      return null;
    }
  },

  async logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(error.message);
  },

  getCurrentUser(): User | null {
    return null;
  },

  async refreshCurrentUser(): Promise<User | null> {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error || !session) return null;

    try {
      const profile = await fetchProfileWithRetry(session.user.id, 2, 300);
      return {
        id: profile.id,
        name: profile.name,
        email: profile.email,
        role: profile.role as UserRole,
        avatarUrl: profile.avatar_url || undefined,
        tenantId: profile.tenant_id,
        isSuperAdmin: profile.is_super_admin || false,
      };
    } catch (err) {
      // Returning null logs the user out client-side; worth knowing about.
      reportError('authService.refreshCurrentUser', err);
      return null;
    }
  },

  // Dev-only: Quick login with email only (for dev menu)
  async devLogin(email: string, password: string = 'password123', expectedTenantId?: string): Promise<User> {
    return this.login(email, password, expectedTenantId);
  }
};
